import React, { useState, useEffect, useRef } from 'react';
import { X, Check, ChevronRight, AlertCircle, FileText, Shield, Users, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Agreement Viewer Modal - Shows agreement content and allows signing
export const AgreementViewerModal = ({ agreement, isOpen, onClose, onSign, isSigning }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agreement?.id]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Allow signing when scrolled to 90% of content
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      setHasScrolledToBottom(true);
    }
  };

  if (!agreement) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {agreement.title}
          </DialogTitle>
          <DialogDescription>
            Version {agreement.version} • Effective {agreement.effective_date}
          </DialogDescription>
        </DialogHeader>
        
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-4"
          style={{ maxHeight: '50vh' }}
        >
          <div 
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: agreement.content }}
          />
        </div>

        <div className="px-6 py-4 border-t bg-gray-50">
          {!hasScrolledToBottom && (
            <p className="text-sm text-amber-600 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Please scroll to the bottom to read the entire agreement
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={() => onSign(agreement.id)}
              disabled={!hasScrolledToBottom || isSigning}
              className="flex-1 gap-2"
            >
              {isSigning ? (
                <>Signing...</>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  I Agree
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Agreements Checklist - Shows required agreements with signing status
export const AgreementsChecklist = ({ agreements, onViewAgreement }) => {
  return (
    <div className="space-y-3">
      {agreements.map((agreement) => (
        <div
          key={agreement.id}
          onClick={() => !agreement.signed && onViewAgreement(agreement.id)}
          className={`p-4 rounded-xl border-2 transition-all ${
            agreement.signed 
              ? 'border-green-200 bg-green-50 cursor-default' 
              : 'border-gray-200 bg-white cursor-pointer hover:border-primary hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                agreement.signed ? 'bg-green-500' : 'bg-gray-200'
              }`}>
                {agreement.signed ? (
                  <Check className="w-4 h-4 text-white" />
                ) : (
                  <FileText className="w-4 h-4 text-gray-500" />
                )}
              </div>
              <div>
                <h3 className={`font-semibold ${agreement.signed ? 'text-green-700' : 'text-gray-900'}`}>
                  {agreement.title}
                </h3>
                {agreement.signed && (
                  <p className="text-xs text-green-600">
                    Signed on {new Date(agreement.signed_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            {!agreement.signed && (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Parent Consent Required Modal - Blocks minor access until parent signs
export const ParentConsentRequiredModal = ({ isOpen, onSubmit, isSubmitting, userName }) => {
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!parentName.trim() || !parentEmail.trim()) {
      toast.error('Please enter both parent name and email');
      return;
    }
    if (!parentEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    onSubmit(parentName.trim(), parentEmail.trim());
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-amber-600" />
          </div>
          <DialogTitle className="text-xl font-bold text-center">
            Parent/Guardian Consent Required
          </DialogTitle>
          <DialogDescription className="text-center">
            Because you are under 18, a parent or guardian must provide consent before you can fully access YAS Connect.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 my-4">
          <p className="text-sm text-blue-800">
            <strong>What happens next?</strong><br />
            We'll send an email to your parent/guardian with a link to review and sign the consent forms. Once they sign, you'll have full access!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="parentName">Parent/Guardian Full Name</Label>
            <Input
              id="parentName"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              placeholder="e.g., Patricia Williams"
              required
            />
          </div>
          <div>
            <Label htmlFor="parentEmail">Parent/Guardian Email</Label>
            <Input
              id="parentEmail"
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              placeholder="e.g., parent@email.com"
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Consent Request'}
          </Button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-4">
          Wrong parent information from signup? No problem - enter the correct details above.
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Parent Consent Pending Modal - Shows when waiting for parent
export const ParentConsentPendingModal = ({ isOpen, parentEmail, onResend, onUpdateParent, onComplete }) => {
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [newParentName, setNewParentName] = useState('');
  const [newParentEmail, setNewParentEmail] = useState('');

  const handleUpdate = () => {
    if (!newParentName.trim() || !newParentEmail.trim()) {
      toast.error('Please enter both parent name and email');
      return;
    }
    onUpdateParent(newParentName.trim(), newParentEmail.trim());
    setShowUpdateForm(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <DialogTitle className="text-xl font-bold text-center">
            Waiting for Parent Consent
          </DialogTitle>
          <DialogDescription className="text-center">
            We've sent a consent request to <strong>{parentEmail}</strong>. Once your parent/guardian signs the forms, you'll have full access!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={onResend}
          >
            Resend Consent Email
          </Button>

          {!showUpdateForm ? (
            <Button 
              variant="ghost" 
              className="w-full text-sm"
              onClick={() => setShowUpdateForm(true)}
            >
              Wrong email? Update parent information
            </Button>
          ) : (
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
              <Input
                value={newParentName}
                onChange={(e) => setNewParentName(e.target.value)}
                placeholder="Parent/Guardian Name"
              />
              <Input
                type="email"
                value={newParentEmail}
                onChange={(e) => setNewParentEmail(e.target.value)}
                placeholder="Parent/Guardian Email"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowUpdateForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUpdate} className="flex-1">
                  Update & Resend
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4">
          <p className="text-xs text-amber-700">
            <strong>Limited Access:</strong> While waiting, you can view some parts of YAS Connect but won't be able to submit journals, goals, or other content.
          </p>
        </div>

        <Button 
          onClick={() => onComplete?.()}
          className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl"
        >
          Continue with Limited Access
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Main Agreements Flow Component
const AgreementsFlow = ({ user, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [agreementsData, setAgreementsData] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmittingParent, setIsSubmittingParent] = useState(false);

  // Skip entirely if on a public page
  const publicPaths = ['/parent-about', '/parent-how-to', '/parent-consent', '/how-to-use', '/preview/', '/login', '/register', '/forgot-password', '/reset-password', '/tutorial'];
  const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p));
  
  useEffect(() => {
    if (!isPublicPage) fetchAgreementStatus();
    else setLoading(false);
  }, []); // eslint-disable-line

  if (isPublicPage) return null;

  const fetchAgreementStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/required/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAgreementsData(data);
        
        // If all agreements signed and no parent consent needed, complete
        if (data.can_access_platform) {
          onComplete?.();
        }
      }
    } catch (error) {
      console.error('Failed to fetch agreements:', error);
      toast.error('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreementContent = async (agreementId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/${agreementId}`
      );
      if (res.ok) {
        const data = await res.json();
        setSelectedAgreement(data);
      }
    } catch (error) {
      toast.error('Failed to load agreement');
    }
  };

  const signAgreement = async (agreementId) => {
    setIsSigning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/sign/${agreementId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ agreement_id: agreementId })
        }
      );
      
      if (res.ok) {
        toast.success('Agreement signed!');
        setSelectedAgreement(null);
        fetchAgreementStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to sign agreement');
      }
    } catch (error) {
      toast.error('Failed to sign agreement');
    } finally {
      setIsSigning(false);
    }
  };

  const requestParentConsent = async (parentName, parentEmail) => {
    setIsSubmittingParent(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/request-parent-consent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            youth_id: user.id,
            parent_email: parentEmail,
            parent_name: parentName
          })
        }
      );
      
      if (res.ok) {
        toast.success('Consent request sent to your parent!');
        fetchAgreementStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send consent request');
      }
    } catch (error) {
      toast.error('Failed to send consent request');
    } finally {
      setIsSubmittingParent(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!agreementsData) return null;

  // If all signed and can access, return null (parent component handles)
  if (agreementsData.can_access_platform) {
    return null;
  }

  // Check if still need to sign agreements
  const unsignedAgreements = agreementsData.agreements.filter(a => !a.signed);
  
  // If has unsigned agreements, show agreements page
  if (unsignedAgreements.length > 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome to YAS Connect!
            </h1>
            <p className="text-gray-600 mt-2">
              Please review and agree to the following documents to continue
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Required Agreements</h2>
              <span className="text-sm text-gray-500">
                {agreementsData.agreements.filter(a => a.signed).length} / {agreementsData.agreements.length}
              </span>
            </div>
            
            <AgreementsChecklist 
              agreements={agreementsData.agreements}
              onViewAgreement={fetchAgreementContent}
            />
          </div>

          {agreementsData.is_minor && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Parent Consent Required</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    After you sign these agreements, we'll need your parent or guardian to sign a consent form.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <AgreementViewerModal
          agreement={selectedAgreement}
          isOpen={!!selectedAgreement}
          onClose={() => setSelectedAgreement(null)}
          onSign={signAgreement}
          isSigning={isSigning}
        />

        <div className="text-center mt-6">
          <button onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
            className="text-xs text-gray-400 hover:text-gray-600 underline">
            Switch Account / Logout
          </button>
        </div>
      </div>
    );
  }

  // All agreements signed but needs parent consent
  if (agreementsData.needs_parent_consent && !agreementsData.parent_consent_completed) {
    // Check if parent consent already requested (has parent email on file)
    const hasParentEmail = user.parent_email && user.parent_consent_status === 'pending';
    
    if (hasParentEmail) {
      return (
        <ParentConsentPendingModal
          isOpen={true}
          parentEmail={user.parent_email}
          onResend={() => requestParentConsent(user.parent_name, user.parent_email)}
          onUpdateParent={requestParentConsent}
          onComplete={onComplete}
        />
      );
    } else {
      return (
        <ParentConsentRequiredModal
          isOpen={true}
          userName={user.name}
          onSubmit={requestParentConsent}
          isSubmitting={isSubmittingParent}
        />
      );
    }
  }

  return null;
};

export default AgreementsFlow;
